export interface LocationData {
  slug: string;
  cityName: string;
  regionName: string;

  // SEO
  title: string;
  metaDescription: string;
  keywords: string[];
  canonicalPath: string;

  // JSON-LD
  areaServed: Array<{ type: string; name: string }>;
  schemaId: string;
  schemaDescription: string;

  // Hero
  heroHeading: string;
  heroSubheading: string;
  heroBadge: string;
  heroParagraph: string;

  // Local context
  localContextHeading: string;
  localContextParagraphs: string[];

  // Industries
  industries: Array<{ name: string; description: string; icon: string }>;

  // Nearby cities
  nearbyCities: Array<{ name: string; description: string }>;

  // Services with city-flavored descriptions
  services: Array<{
    title: string;
    description: string;
    link: string;
    icon: string;
  }>;

  // Trust signals
  trustSignals: Array<{ label: string; value: string }>;

  // Related blog slugs
  relatedBlogSlugs: string[];

  // CTA
  ctaHeading: string;
  ctaText: string;
}

export const locations: Record<string, LocationData> = {
  orlando: {
    slug: 'orlando',
    cityName: 'Orlando',
    regionName: 'Orange County',

    // SEO
    title: 'Web Design & Software Development in Orlando FL | KG ProDesign',
    metaDescription:
      'Custom web design and software development for Orlando businesses. Award-winning developer with 18+ years experience. Free consultation.',
    keywords: [
      'web design orlando',
      'software development orlando',
      'web developer orlando',
      'custom software orlando',
      'orlando web design company',
      'app development orlando',
    ],
    canonicalPath: '/locations/orlando',

    // JSON-LD
    areaServed: [
      { type: 'City', name: 'Orlando' },
      { type: 'City', name: 'Winter Park' },
      { type: 'City', name: 'Maitland' },
      { type: 'City', name: 'Dr. Phillips' },
      { type: 'City', name: 'Winter Garden' },
      { type: 'City', name: 'Windermere' },
      { type: 'State', name: 'Florida' },
      { type: 'Country', name: 'US' },
    ],
    schemaId: 'https://kgprodesign.com/locations/orlando',
    schemaDescription:
      'KG ProDesign provides custom web design, software development, consulting, and AI automation services to businesses in Orlando and the greater Orange County area.',

    // Hero
    heroBadge: 'ORLANDO, FL',
    heroHeading: 'Web Design & Software Development in Orlando',
    heroSubheading:
      'Award-winning design. 18+ years of enterprise software expertise. Based right here in Central Florida.',
    heroParagraph:
      "Orlando's economy has grown far beyond tourism. Healthcare systems, defense contractors, fintech startups, and professional services firms across Orange County need technology partners who understand the local market. KG ProDesign delivers custom software, web design, and AI automation to Orlando businesses -- from downtown startups to established firms in Winter Park and Dr. Phillips.",

    // Local context
    localContextHeading: 'Why Orlando Businesses Choose KG ProDesign',
    localContextParagraphs: [
      "Orlando has quietly become one of the most diversified economies in the Southeast. The defense and simulation corridor stretching from Research Parkway to Lake Nona attracts billions in federal contracts. UCF's innovation district has spawned a generation of startups that need real software -- not Squarespace pages. Healthcare systems like AdventHealth and Orlando Health are scaling digital patient experiences, and every one of those organizations needs technology partners who can deliver at the enterprise level while understanding the local business landscape.",
      "The competitive reality for Orlando businesses is clear: template websites and off-the-shelf software no longer differentiate you. Whether you run a boutique consulting firm on Park Avenue in Winter Park, a medical practice in Dr. Phillips, or a restaurant group along International Drive, your customers expect polished digital experiences. KG ProDesign builds custom solutions that match the ambition of Orlando's business community.",
      'We bring 18+ years of enterprise software experience to every engagement, and our track record speaks for itself. In 2016, we won a Scovie Award for the marketing website we designed for Goldson\'s MoreFire Hot Sauce -- a project that demonstrated what happens when serious design craft meets strategic thinking. That same approach drives every project we take on in the Orlando market.',
    ],

    // Industries
    industries: [
      {
        name: 'Tourism & Hospitality',
        description:
          'Custom booking platforms, guest experience apps, and revenue management tools for hotels, resorts, and attractions across the Orlando tourism corridor.',
        icon: 'apartment',
      },
      {
        name: 'Healthcare Systems',
        description:
          'HIPAA-compliant patient portals, appointment scheduling software, and internal workflow tools for hospitals, clinics, and medical practices.',
        icon: 'local_hospital',
      },
      {
        name: 'Professional Services',
        description:
          'Client portals, proposal generators, and CRM integrations for law firms, accounting practices, and consulting companies.',
        icon: 'business_center',
      },
      {
        name: 'Real Estate & Construction',
        description:
          'Property listing platforms, project management dashboards, and lead generation websites for developers, builders, and brokerages.',
        icon: 'construction',
      },
      {
        name: 'Restaurant & Food Service',
        description:
          'Online ordering systems, menu management platforms, and branded websites that drive reservations and takeout orders.',
        icon: 'restaurant',
      },
      {
        name: 'E-Commerce & Retail',
        description:
          'Custom storefronts, inventory management systems, and omnichannel solutions for Orlando-based retailers expanding their digital reach.',
        icon: 'storefront',
      },
    ],

    // Nearby cities
    nearbyCities: [
      {
        name: 'Winter Park',
        description:
          'Boutique businesses and galleries along Park Avenue',
      },
      {
        name: 'Maitland',
        description:
          'Professional services and growing tech companies',
      },
      {
        name: 'Dr. Phillips',
        description:
          'Medical practices and luxury service providers',
      },
      {
        name: 'Winter Garden',
        description:
          'Growing suburban businesses and meal prep companies',
      },
      {
        name: 'Windermere',
        description: 'Custom home builders and luxury services',
      },
    ],

    // Services
    services: [
      {
        title: 'Software Development',
        description:
          "Full-stack custom software for Orlando businesses -- from internal workflow tools for defense contractors to patient-facing healthcare apps. We build systems that scale with Orange County's fastest-growing industries.",
        link: '/services/software-development',
        icon: 'computer',
      },
      {
        title: 'Web Design',
        description:
          'Custom websites for Orlando businesses that outperform templates. From Winter Park boutiques to downtown startups, we build sites that convert visitors into customers.',
        link: '/services/web-design',
        icon: 'design_services',
      },
      {
        title: 'Consulting',
        description:
          'Technology strategy for Orlando companies navigating digital transformation. We help you choose the right platforms, plan migrations, and avoid costly mistakes.',
        link: '/services/consulting',
        icon: 'psychology',
      },
      {
        title: 'Automation & AI',
        description:
          'Streamline operations with intelligent automation. Orlando businesses use our AI solutions to reduce manual work, improve accuracy, and free up their teams for higher-value tasks.',
        link: '/services/automation-ai',
        icon: 'smart_toy',
      },
    ],

    // Trust signals
    trustSignals: [
      { label: 'Years Experience', value: '18+' },
      { label: 'Award-Winning', value: 'Web Design' },
      { label: 'Based In', value: 'Central Florida' },
      { label: 'Projects Delivered', value: '50+' },
    ],

    // Related blog slugs
    relatedBlogSlugs: [
      'orlando-central-florida-businesses-custom-websites',
      'how-to-choose-software-developer-central-florida',
      'small-business-tech-oviedo-orlando-tampa-2026',
    ],

    // CTA
    ctaHeading: 'Ready to Build Something Great in Orlando?',
    ctaText:
      'From downtown Orlando to Winter Park, we help local businesses build custom software and websites that drive real results.',
  },

  oviedo: {
    slug: 'oviedo',
    cityName: 'Oviedo',
    regionName: 'Seminole County',

    // SEO
    title: 'Web Design & Software Development in Oviedo FL | KG ProDesign',
    metaDescription:
      'Your local Oviedo web designer and software developer. Award-winning, 18+ years experience. Serving Seminole County businesses.',
    keywords: [
      'web design oviedo',
      'software development oviedo',
      'web developer oviedo fl',
      'oviedo web design',
      'seminole county web developer',
      'software developer near me oviedo',
    ],
    canonicalPath: '/locations/oviedo',

    // JSON-LD
    areaServed: [
      { type: 'City', name: 'Oviedo' },
      { type: 'City', name: 'Winter Springs' },
      { type: 'City', name: 'Casselberry' },
      { type: 'City', name: 'Altamonte Springs' },
      { type: 'City', name: 'Sanford' },
      { type: 'City', name: 'Lake Mary' },
      { type: 'City', name: 'Longwood' },
      { type: 'State', name: 'Florida' },
      { type: 'Country', name: 'US' },
    ],
    schemaId: 'https://kgprodesign.com/locations/oviedo',
    schemaDescription:
      'KG ProDesign is an Oviedo-based web design and software development company serving businesses throughout Seminole County and Central Florida.',

    // Hero
    heroBadge: 'OVIEDO, FL -- OUR HOME BASE',
    heroHeading: 'Web Design & Software Development in Oviedo',
    heroSubheading:
      'Your neighbor, not a remote agency. Award-winning design and 18+ years of enterprise software expertise.',
    heroParagraph:
      'KG ProDesign is based right here in Oviedo. We are not a faceless agency in another state -- we are your neighbors. We eat at the restaurants along Alafaya Trail, shop at Oviedo on the Park, and understand what Seminole County businesses need to compete in a digital-first world. From Winter Springs to Lake Mary, we build custom software and websites that help local businesses grow.',

    // Local context
    localContextHeading: 'Built in Oviedo, For Oviedo',
    localContextParagraphs: [
      "Oviedo has transformed over the past decade from a quiet Orlando suburb into a thriving business community in its own right. Oviedo on the Park brought a new energy to the city center, and Alafaya Trail has become a corridor of restaurants, medical offices, salons, and professional services that serve tens of thousands of residents. But most of these businesses still rely on generic templates or outdated websites that don't reflect the quality of their work. That gap between service quality and digital presence is exactly what we close.",
      "The broader Seminole County business ecosystem amplifies the opportunity. Lake Mary's tech corridor is home to established companies and growing startups alike, creating a ripple effect of demand for custom software and modern web experiences. Winter Springs service businesses, Casselberry retail shops, and Altamonte Springs medical practices all need technology that works as hard as they do. Being based in Oviedo means we understand the local market at a level that remote agencies simply cannot match.",
      'We won a Scovie Award in 2016 for the marketing website we designed for Goldson\'s MoreFire Hot Sauce -- and that same award-winning design approach is what we bring to every Oviedo business we work with. When you hire KG ProDesign, you are hiring a neighbor with 18+ years of enterprise software experience who genuinely cares about seeing Seminole County businesses succeed online.',
    ],

    // Industries
    industries: [
      {
        name: 'Restaurants & Local Dining',
        description:
          'Online ordering, menu platforms, and branded websites for the restaurants and eateries that line Alafaya Trail and Oviedo on the Park.',
        icon: 'restaurant',
      },
      {
        name: 'Professional Services',
        description:
          'Client portals, scheduling systems, and lead-generating websites for accountants, attorneys, financial advisors, and consultants across Seminole County.',
        icon: 'business_center',
      },
      {
        name: 'Retail & E-Commerce',
        description:
          'Custom online stores and inventory management for local retailers expanding beyond their physical storefronts.',
        icon: 'storefront',
      },
      {
        name: 'Healthcare Providers',
        description:
          'Patient intake forms, appointment booking systems, and compliant websites for medical offices, dental practices, and therapists.',
        icon: 'local_hospital',
      },
      {
        name: 'Construction & Home Services',
        description:
          'Lead generation websites, project galleries, and estimate request systems for contractors, landscapers, and home service companies.',
        icon: 'home_repair_service',
      },
      {
        name: 'Education & Tutoring',
        description:
          'Booking platforms, student portals, and marketing websites for tutoring centers, enrichment programs, and private educators.',
        icon: 'school',
      },
    ],

    // Nearby cities
    nearbyCities: [
      {
        name: 'Winter Springs',
        description:
          'Growing service businesses and family-owned companies',
      },
      {
        name: 'Casselberry',
        description:
          'Retail and service-based businesses along SR 436',
      },
      {
        name: 'Altamonte Springs',
        description: 'Medical practices and professional offices',
      },
      {
        name: 'Sanford',
        description:
          'Revitalized downtown with new restaurants and shops',
      },
      {
        name: 'Lake Mary',
        description:
          'Established tech corridor with professional firms',
      },
    ],

    // Services
    services: [
      {
        title: 'Software Development',
        description:
          'Custom software built by your Oviedo neighbor. From patient management systems for Seminole County medical offices to inventory tools for Lake Mary businesses, we build software that solves real problems.',
        link: '/services/software-development',
        icon: 'computer',
      },
      {
        title: 'Web Design',
        description:
          'Custom websites for Oviedo businesses -- from restaurants on Alafaya Trail to professional offices in Lake Mary. Your neighbor building your digital presence.',
        link: '/services/web-design',
        icon: 'design_services',
      },
      {
        title: 'Consulting',
        description:
          'Technology guidance from someone who understands Seminole County. We help local businesses make smart technology decisions without overspending or overcomplicating.',
        link: '/services/consulting',
        icon: 'psychology',
      },
      {
        title: 'Automation & AI',
        description:
          'Automate the repetitive work that slows down your Oviedo business. From invoice processing to customer follow-ups, we build intelligent systems that save you hours every week.',
        link: '/services/automation-ai',
        icon: 'smart_toy',
      },
    ],

    // Trust signals
    trustSignals: [
      { label: 'Years Experience', value: '18+' },
      { label: 'Award-Winning', value: 'Web Design' },
      { label: 'Location', value: 'Oviedo, FL' },
      { label: 'Projects Delivered', value: '50+' },
    ],

    // Related blog slugs
    relatedBlogSlugs: [
      'small-business-tech-oviedo-orlando-tampa-2026',
      'how-to-choose-software-developer-central-florida',
      'orlando-central-florida-businesses-custom-websites',
    ],

    // CTA
    ctaHeading: 'Ready to Work with Your Local Oviedo Developer?',
    ctaText:
      'We are right here in Oviedo. Let us show you what a local technology partner can do for your business.',
  },

  tampa: {
    slug: 'tampa',
    cityName: 'Tampa',
    regionName: 'Hillsborough County',

    // SEO
    title: 'Web Design & Software Development in Tampa FL | KG ProDesign',
    metaDescription:
      'Custom web design and software development for Tampa Bay businesses. Award-winning developer serving Tampa, St. Pete, and beyond.',
    keywords: [
      'web design tampa',
      'software development tampa',
      'web developer tampa fl',
      'tampa web design company',
      'tampa bay software developer',
      'app development tampa',
    ],
    canonicalPath: '/locations/tampa',

    // JSON-LD
    areaServed: [
      { type: 'City', name: 'Tampa' },
      { type: 'City', name: 'St. Petersburg' },
      { type: 'City', name: 'Brandon' },
      { type: 'City', name: 'Lakeland' },
      { type: 'City', name: 'Clearwater' },
      { type: 'State', name: 'Florida' },
      { type: 'Country', name: 'US' },
    ],
    schemaId: 'https://kgprodesign.com/locations/tampa',
    schemaDescription:
      'KG ProDesign delivers custom web design, software development, and AI automation to Tampa Bay businesses from its Central Florida base.',

    // Hero
    heroBadge: 'TAMPA BAY AREA',
    heroHeading: 'Web Design & Software Development in Tampa',
    heroSubheading:
      'Award-winning design. 18+ years of enterprise expertise. Serving the entire Tampa Bay region.',
    heroParagraph:
      "Tampa Bay has emerged as one of Florida's most dynamic technology markets. From fintech startups downtown to healthcare systems in Brandon, creative studios in St. Petersburg to logistics companies in Lakeland, the region's businesses need more than template websites. KG ProDesign brings Central Florida-based expertise to the Tampa Bay market with custom software, web design, and AI automation built for real business growth.",

    // Local context
    localContextHeading: 'Why Tampa Bay Businesses Choose KG ProDesign',
    localContextParagraphs: [
      "Tampa Bay's technology scene has exploded. The Water Street district downtown has attracted fintech companies, venture capital, and a wave of tech talent relocating from more expensive markets. But the growth extends well beyond downtown -- Brandon's healthcare providers are modernizing patient experiences, St. Petersburg's creative economy demands sophisticated digital platforms, and Lakeland's logistics sector needs custom fleet management and warehouse tools. This is a region where businesses are scaling fast and need technology partners who can keep pace.",
      "The problem most Tampa Bay businesses face is familiar: agencies that oversell and underdeliver, template sites that look like everyone else's, and offshore development teams that disappear after launch. KG ProDesign operates differently. We are based in Central Florida -- just an hour down I-4 -- and we bring 18+ years of enterprise software experience to every engagement. When we build your system, we are accountable. We answer our messages. We maintain what we build.",
      'Our design credentials are proven. We won a Scovie Award in 2016 for the marketing website we created for Goldson\'s MoreFire Hot Sauce, demonstrating the kind of design craft and strategic thinking that Tampa Bay businesses deserve. Whether you are a fintech startup in Channelside, a restaurant group in Ybor City, or an HVAC company serving Hillsborough County, we build solutions that match the scale of your ambition.',
    ],

    // Industries
    industries: [
      {
        name: 'Fintech & Financial Services',
        description:
          'Secure dashboards, payment platforms, and compliance-ready applications for the growing cluster of financial technology companies in downtown Tampa.',
        icon: 'payments',
      },
      {
        name: 'Healthcare Technology',
        description:
          'Patient portals, telehealth integrations, and clinical workflow tools for hospitals, specialty practices, and urgent care facilities across Tampa Bay.',
        icon: 'local_hospital',
      },
      {
        name: 'Creative & Design Studios',
        description:
          "Portfolio platforms, client proofing tools, and branded websites for the creative agencies and independent studios thriving in St. Petersburg's arts district.",
        icon: 'design_services',
      },
      {
        name: 'Logistics & Distribution',
        description:
          "Fleet tracking dashboards, warehouse management systems, and route optimization tools for Lakeland's distribution hub and Tampa's port-adjacent logistics firms.",
        icon: 'local_shipping',
      },
      {
        name: 'Home Services & HVAC',
        description:
          'Lead generation websites, dispatch scheduling systems, and customer management portals for contractors and home service companies serving Hillsborough County.',
        icon: 'home_repair_service',
      },
      {
        name: 'Restaurant & Hospitality',
        description:
          'Online ordering platforms, reservation systems, and branded digital experiences for the restaurant scenes in Ybor City, SoHo, and across Tampa Bay.',
        icon: 'restaurant',
      },
    ],

    // Nearby cities
    nearbyCities: [
      {
        name: 'St. Petersburg',
        description:
          'Creative economy with independent retailers and design studios',
      },
      {
        name: 'Brandon',
        description:
          'Service-based businesses and growing suburban market',
      },
      {
        name: 'Lakeland',
        description:
          'Logistics, distribution, and fleet management companies',
      },
      {
        name: 'Clearwater',
        description:
          'Tourism-adjacent businesses and service companies',
      },
    ],

    // Services
    services: [
      {
        title: 'Software Development',
        description:
          "Custom software for Tampa Bay's fastest-moving industries. From fintech platforms in Water Street to fleet management tools in Lakeland, we build systems that handle real-world complexity.",
        link: '/services/software-development',
        icon: 'computer',
      },
      {
        title: 'Web Design',
        description:
          'Custom websites for Tampa Bay businesses -- from fintech dashboards downtown to restaurant sites in Ybor City. Built for the Tampa market, not recycled from a template library.',
        link: '/services/web-design',
        icon: 'design_services',
      },
      {
        title: 'Consulting',
        description:
          'Technology strategy for Tampa Bay companies scaling beyond their current systems. We help you evaluate platforms, plan architecture, and execute migrations without disrupting operations.',
        link: '/services/consulting',
        icon: 'psychology',
      },
      {
        title: 'Automation & AI',
        description:
          'Intelligent automation for Tampa Bay businesses drowning in manual processes. From document processing to customer service workflows, we build AI systems that deliver measurable ROI.',
        link: '/services/automation-ai',
        icon: 'smart_toy',
      },
    ],

    // Trust signals
    trustSignals: [
      { label: 'Years Experience', value: '18+' },
      { label: 'Award-Winning', value: 'Web Design' },
      { label: 'Serving', value: 'Tampa Bay Area' },
      { label: 'Projects Delivered', value: '50+' },
    ],

    // Related blog slugs
    relatedBlogSlugs: [
      'small-business-tech-oviedo-orlando-tampa-2026',
      'orlando-central-florida-businesses-custom-websites',
      'how-to-choose-software-developer-central-florida',
    ],

    // CTA
    ctaHeading: 'Ready to Build Something Great in Tampa Bay?',
    ctaText:
      'From downtown Tampa to St. Petersburg, we help Tampa Bay businesses build custom software and websites that drive results.',
  },

  cocoa: {
    slug: 'cocoa',
    cityName: 'Cocoa',
    regionName: 'Space Coast',

    // SEO
    title: 'Web Design & Software Development on the Space Coast FL | KG ProDesign',
    metaDescription:
      'Custom web design and software development for Space Coast businesses in Cocoa, Melbourne, Palm Bay, and Brevard County.',
    keywords: [
      'web design cocoa fl',
      'software development space coast',
      'web developer melbourne fl',
      'palm bay web design',
      'brevard county software developer',
      'space coast web design',
    ],
    canonicalPath: '/locations/cocoa',

    // JSON-LD
    areaServed: [
      { type: 'City', name: 'Cocoa' },
      { type: 'City', name: 'Melbourne' },
      { type: 'City', name: 'Palm Bay' },
      { type: 'City', name: 'Viera' },
      { type: 'City', name: 'Rockledge' },
      { type: 'City', name: 'Titusville' },
      { type: 'City', name: 'Merritt Island' },
      { type: 'State', name: 'Florida' },
      { type: 'Country', name: 'US' },
    ],
    schemaId: 'https://kgprodesign.com/locations/cocoa/#business',
    schemaDescription:
      'Custom web design and software development for Space Coast businesses in Cocoa, Melbourne, Palm Bay, and Brevard County, FL.',

    // Hero
    heroBadge: 'SPACE COAST, FL',
    heroHeading: "Web Design & Software Development on Florida's Space Coast",
    heroSubheading:
      'Award-winning design and 18+ years of enterprise expertise. Serving Cocoa, Melbourne, Palm Bay, and all of Brevard County.',
    heroParagraph:
      "The Space Coast runs on a unique economic engine. Kennedy Space Center, SpaceX, Blue Origin, and L3Harris Technologies anchor a corridor where aerospace innovation meets everyday commerce. Defense contractors, healthcare systems like Health First, a thriving cruise port, and miles of beachfront tourism create an economy unlike anywhere else in Florida. But the local businesses that serve this market -- the restaurants near Port Canaveral, the medical practices expanding in Viera, the professional firms along US-1 -- need more than cookie-cutter templates. They need a technology partner who understands how aerospace-driven growth shapes what customers expect from every business in Brevard County.",

    // Local context
    localContextHeading: 'Why Space Coast Businesses Choose KG ProDesign',
    localContextParagraphs: [
      "Brevard County is in the middle of a growth surge that has fundamentally changed the local business landscape. SpaceX launches have become routine, Blue Origin is building its orbital launch complex, Northrop Grumman maintains a significant manufacturing presence, and L3Harris Technologies employs thousands across Melbourne and Palm Bay. That aerospace and defense concentration has created a spillover effect that touches every sector: restaurants near the cruise terminal at Port Canaveral are busier than ever, medical practices in Viera's master-planned healthcare campus are expanding, downtown Melbourne's arts district has attracted boutique retailers and professional services, and Rockledge's US-1 corridor continues to add commercial tenants. The Space Coast is no longer just a launch-watching destination -- it is a full-scale regional economy that demands serious digital infrastructure.",
      "The challenge for local businesses is that they are competing for online attention in a market shaped by aerospace-level expectations. When your customers work at companies that build rockets and satellite systems, a dated website or clunky booking system stands out for all the wrong reasons. At the same time, the tourist traffic flowing through Port Canaveral and Cocoa Beach means your digital presence has to convert first-time visitors who have never heard of your business. That dual audience -- high-tech professionals and transient tourists -- requires strategic design thinking, not a template with your logo swapped in.",
      "KG ProDesign brings 18+ years of enterprise software experience to the Space Coast market, and our work speaks for itself. We won a Scovie Award in 2016 for the marketing website we designed for Goldson's MoreFire Hot Sauce -- the kind of design craft and strategic execution that Brevard County businesses deserve. Located just 45 minutes west of the Space Coast in our home base of Oviedo, we are close enough to understand your market and experienced enough to deliver solutions that match the caliber of the companies driving this region forward.",
    ],

    // Industries
    industries: [
      {
        name: 'Aerospace & Defense Technology',
        description:
          'Internal tools, contractor portals, and secure web applications for the aerospace and defense companies that anchor the Space Coast economy.',
        icon: 'rocket_launch',
      },
      {
        name: 'Healthcare & Medical',
        description:
          'Patient portals, appointment systems, and compliant websites for the Health First corridor and the growing network of medical practices across Viera and Melbourne.',
        icon: 'local_hospital',
      },
      {
        name: 'Tourism & Cruise Industry',
        description:
          'Booking platforms, visitor guides, and branded websites for businesses serving Port Canaveral cruise passengers and Cocoa Beach tourists.',
        icon: 'sailing',
      },
      {
        name: 'Restaurant & Hospitality',
        description:
          'Online ordering, reservation systems, and branded digital experiences for beachside restaurants in Cocoa Beach, downtown Melbourne eateries, and hotel properties near the port.',
        icon: 'restaurant',
      },
      {
        name: 'Professional Services',
        description:
          'Client portals, lead generation websites, and CRM integrations for law firms, financial advisors, and consulting companies throughout Brevard County.',
        icon: 'business_center',
      },
      {
        name: 'Retail & E-Commerce',
        description:
          'Custom storefronts and inventory management for Space Coast retailers expanding their digital reach beyond their physical locations.',
        icon: 'storefront',
      },
    ],

    // Nearby cities
    nearbyCities: [
      {
        name: 'Melbourne',
        description:
          'Downtown arts district, professional services, and growing tech companies',
      },
      {
        name: 'Palm Bay',
        description:
          'Largest city in Brevard with expanding commercial corridor',
      },
      {
        name: 'Viera',
        description:
          'Master-planned community with healthcare campus and retail growth',
      },
      {
        name: 'Rockledge',
        description:
          'Professional offices and service-based businesses along US-1',
      },
      {
        name: 'Titusville',
        description:
          'Gateway to Kennedy Space Center with revitalized downtown',
      },
      {
        name: 'Merritt Island',
        description:
          'Residential and commercial hub between the mainland and the beach',
      },
    ],

    // Services
    services: [
      {
        title: 'Software Development',
        description:
          "Custom software for Space Coast businesses navigating rapid growth. From contractor management tools for aerospace suppliers to patient intake systems for Viera medical offices, we build applications that handle Brevard County's real-world complexity.",
        link: '/services/software-development',
        icon: 'computer',
      },
      {
        title: 'Web Design',
        description:
          'Custom websites for Space Coast businesses -- from beachside restaurants in Cocoa Beach to aerospace contractors in Melbourne. Built to compete in Brevard County\'s growing market.',
        link: '/services/web-design',
        icon: 'design_services',
      },
      {
        title: 'Consulting',
        description:
          'Technology strategy for Space Coast companies outgrowing their current systems. We help you evaluate platforms, plan migrations, and make smart technology investments without disrupting operations.',
        link: '/services/consulting',
        icon: 'psychology',
      },
      {
        title: 'Automation & AI',
        description:
          'Intelligent automation for Brevard County businesses buried in manual processes. From document workflows to customer follow-up sequences, we build AI systems that free your team to focus on growth.',
        link: '/services/automation-ai',
        icon: 'smart_toy',
      },
    ],

    // Trust signals
    trustSignals: [
      { label: 'Years Experience', value: '18+' },
      { label: 'Award-Winning', value: 'Web Design' },
      { label: 'Serving', value: 'Space Coast / Brevard' },
      { label: 'Projects Delivered', value: '50+' },
    ],

    // Related blog slugs
    relatedBlogSlugs: [
      'orlando-central-florida-businesses-custom-websites',
      'how-to-choose-software-developer-central-florida',
      'small-business-tech-oviedo-orlando-tampa-2026',
    ],

    // CTA
    ctaHeading: 'Ready to Build Something Great on the Space Coast?',
    ctaText:
      'From Cocoa to Melbourne to Palm Bay, we help Space Coast businesses build custom software and websites that stand out in Brevard County.',
  },

  daytona: {
    slug: 'daytona',
    cityName: 'Daytona Beach',
    regionName: 'Volusia County',

    // SEO
    title: 'Web Design & Software Development in Daytona Beach FL | KG ProDesign',
    metaDescription:
      'Custom web design and software development for Daytona Beach and Volusia County businesses. Award-winning, 18+ years experience.',
    keywords: [
      'web design daytona beach',
      'software development daytona fl',
      'web developer volusia county',
      'daytona beach web design',
      'ormond beach web developer',
      'port orange software development',
    ],
    canonicalPath: '/locations/daytona',

    // JSON-LD
    areaServed: [
      { type: 'City', name: 'Daytona Beach' },
      { type: 'City', name: 'Ormond Beach' },
      { type: 'City', name: 'Port Orange' },
      { type: 'City', name: 'DeLand' },
      { type: 'City', name: 'New Smyrna Beach' },
      { type: 'City', name: 'Deltona' },
      { type: 'State', name: 'Florida' },
      { type: 'Country', name: 'US' },
    ],
    schemaId: 'https://kgprodesign.com/locations/daytona/#business',
    schemaDescription:
      'Custom web design and software development for Daytona Beach and Volusia County businesses.',

    // Hero
    heroBadge: 'DAYTONA BEACH, FL',
    heroHeading: 'Web Design & Software Development in Daytona Beach',
    heroSubheading:
      'Award-winning design and 18+ years of enterprise expertise. Serving Daytona Beach, Ormond Beach, Port Orange, and all of Volusia County.',
    heroParagraph:
      "Daytona Beach is far more than its motorsports heritage. Yes, Daytona International Speedway, Bike Week, and NASCAR define the city's global identity, but the local economy has diversified in ways that most outsiders do not appreciate. AdventHealth and Halifax Health anchor a robust healthcare sector. Embry-Riddle Aeronautical University, Bethune-Cookman University, and Daytona State College form an education ecosystem that drives demand for technology services. The intersection of I-4 and I-95 makes Volusia County a geographic crossroads connecting Orlando, Jacksonville, and the Space Coast. Local businesses operating in this market need a digital presence that matches the energy and ambition of a region that is growing on multiple fronts simultaneously.",

    // Local context
    localContextHeading: 'Why Daytona Beach Businesses Choose KG ProDesign',
    localContextParagraphs: [
      "Volusia County's economy has a depth that surprises people who only associate the area with racing and spring break. AdventHealth Daytona Beach and Halifax Health are investing heavily in expanded facilities and digital patient services. Embry-Riddle Aeronautical University is a top-tier institution that generates a steady pipeline of engineering talent and technology demand. Bethune-Cookman University and Daytona State College round out an education corridor that keeps the workforce pipeline strong. The LPGA has its international headquarters in Daytona Beach, bringing a year-round corporate presence that extends well beyond tournament weekends. All of these anchors create downstream demand for the restaurants, professional services, retail shops, and hospitality businesses that make the local economy run day to day.",
      "The I-4 and I-95 crossroads position gives Volusia County a unique advantage -- and a unique challenge. Businesses here can draw customers from Orlando, Jacksonville, and the Space Coast, but they also compete with companies in those larger markets for online visibility. Ormond Beach's Granada Boulevard corridor is packed with professional services firms that need to rank against Orlando competitors. Port Orange's retail and restaurant scene is growing fast but often gets overlooked in regional search results. DeLand's historic downtown has undergone a genuine revitalization with boutique businesses and Stetson University anchoring the west side of the county. And Deltona, the largest city in Volusia County by population, has an expanding base of suburban businesses that need modern digital tools to reach their customers.",
      "KG ProDesign brings 18+ years of enterprise software experience and a proven design track record to Volusia County. We won a Scovie Award in 2016 for the marketing website we designed for Goldson's MoreFire Hot Sauce -- and that same level of strategic design thinking is what we deliver to every client. Located just up I-4 from our home base in Oviedo, we understand the Central Florida business landscape at a level that agencies from Miami or Atlanta simply cannot match. Whether you run a beachfront restaurant, a medical practice, or a professional services firm, we build technology that helps Daytona Beach businesses compete and win.",
    ],

    // Industries
    industries: [
      {
        name: 'Motorsports & Events Industry',
        description:
          'Event management platforms, ticketing integrations, and branded digital experiences for businesses connected to Daytona International Speedway and the year-round events calendar.',
        icon: 'sports_motorsports',
      },
      {
        name: 'Healthcare Systems',
        description:
          'Patient portals, telehealth platforms, and clinical workflow tools for AdventHealth, Halifax Health, and the network of specialty practices across Volusia County.',
        icon: 'local_hospital',
      },
      {
        name: 'Tourism & Hospitality',
        description:
          'Booking engines, guest experience platforms, and marketing websites for hotels, vacation rentals, and attractions serving Daytona Beach visitors year-round.',
        icon: 'beach_access',
      },
      {
        name: 'Education & University Services',
        description:
          'Student portals, campus tools, and branded websites for businesses and organizations connected to Embry-Riddle, Bethune-Cookman, Stetson, and Daytona State.',
        icon: 'school',
      },
      {
        name: 'Restaurant & Beachfront Dining',
        description:
          'Online ordering, menu management, and reservation systems for the beachfront restaurants, downtown eateries, and growing food scenes across Volusia County.',
        icon: 'restaurant',
      },
      {
        name: 'Professional Services',
        description:
          'Client portals, lead generation websites, and workflow automation for law firms, financial advisors, and consulting companies along the Granada and LPGA corridors.',
        icon: 'business_center',
      },
    ],

    // Nearby cities
    nearbyCities: [
      {
        name: 'Ormond Beach',
        description:
          'Professional services and healthcare along the Granada corridor',
      },
      {
        name: 'Port Orange',
        description:
          'Growing retail and residential market south of Daytona',
      },
      {
        name: 'DeLand',
        description:
          'Historic downtown with boutique businesses and Stetson University',
      },
      {
        name: 'New Smyrna Beach',
        description:
          'Beachfront tourism, restaurants, and arts community',
      },
      {
        name: 'Deltona',
        description:
          'Largest city in Volusia with expanding suburban businesses',
      },
    ],

    // Services
    services: [
      {
        title: 'Software Development',
        description:
          "Custom software for Volusia County businesses scaling beyond spreadsheets and manual processes. From patient management systems for healthcare providers to event logistics tools for motorsports-adjacent companies, we build applications that handle Daytona's unique demands.",
        link: '/services/software-development',
        icon: 'computer',
      },
      {
        title: 'Web Design',
        description:
          'Custom websites for Daytona Beach businesses -- from beachfront restaurants to motorsports-adjacent companies. Built to convert both tourists and year-round customers.',
        link: '/services/web-design',
        icon: 'design_services',
      },
      {
        title: 'Consulting',
        description:
          'Technology strategy for Volusia County businesses ready to modernize. We help you evaluate platforms, plan system upgrades, and execute digital transformation without disrupting your daily operations.',
        link: '/services/consulting',
        icon: 'psychology',
      },
      {
        title: 'Automation & AI',
        description:
          'Intelligent automation for Daytona Beach businesses handling seasonal surges and year-round operations. From automated booking confirmations to inventory management, we build AI systems that keep your business running smoothly.',
        link: '/services/automation-ai',
        icon: 'smart_toy',
      },
    ],

    // Trust signals
    trustSignals: [
      { label: 'Years Experience', value: '18+' },
      { label: 'Award-Winning', value: 'Web Design' },
      { label: 'Serving', value: 'Volusia County' },
      { label: 'Projects Delivered', value: '50+' },
    ],

    // Related blog slugs
    relatedBlogSlugs: [
      'orlando-central-florida-businesses-custom-websites',
      'small-business-tech-oviedo-orlando-tampa-2026',
      'how-to-choose-software-developer-central-florida',
    ],

    // CTA
    ctaHeading: 'Ready to Build Something Great in Daytona Beach?',
    ctaText:
      'From the Speedway to the beach, we help Volusia County businesses build custom software and websites that drive real results.',
  },
};
